import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger} from '@/components/ui/dialog'
import {Label} from '@/components/ui/label'
import {CatalogOtherField} from '@/components/catalog-other-field'
import {DateField} from '@/components/date-field'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {useTagCatalog} from '@/hooks/empresa/organizacion/use-tags'
import {useUpdateEmployeeFicha, type Ficha, type FichaOperadorUpdate} from '@/hooks/empresa/identidad/use-ficha'
import {
  AFP_OPTIONS,
  ARL_OPTIONS,
  CONTRACT_TYPES,
  EPS_OPTIONS,
  GENERO,
  JORNADA_TYPES,
  NIVEL_EDUCATIVO,
} from '@/lib/ficha-catalogs'
import {localToday} from '@/lib/risk'
import {translateApiError} from '@/lib/errors'

type FichaFormState = {
  fecha_ingreso: string
  tipo_vinculacion: string
  jornada_turno: string
  cargo: string
  cargo_descripcion: string
  area: string
  sede: string
  nivel_educativo: string
  genero: string
  fecha_nacimiento: string
  eps: string
  afp: string
  arl: string
}

function fichaToForm(ficha: Ficha): FichaFormState {
  return {
    fecha_ingreso: ficha.fecha_ingreso ?? '',
    tipo_vinculacion: ficha.tipo_vinculacion ?? '',
    jornada_turno: ficha.jornada_turno ?? '',
    cargo: ficha.cargo ?? '',
    cargo_descripcion: ficha.cargo_descripcion ?? '',
    area: ficha.area ?? '',
    sede: ficha.sede ?? '',
    nivel_educativo: ficha.nivel_educativo ?? '',
    genero: ficha.genero ?? '',
    fecha_nacimiento: ficha.fecha_nacimiento ?? '',
    eps: ficha.eps ?? '',
    afp: ficha.afp ?? '',
    arl: ficha.arl ?? '',
  }
}

function validateFechaNacimiento(raw: string): string | null {
  return !raw || raw <= localToday() ? null : 'errors.futureDateNotAllowed'
}

function fichaFormToPayload(form: FichaFormState): FichaOperadorUpdate {
  return {
    fecha_ingreso: form.fecha_ingreso || null,
    tipo_vinculacion: form.tipo_vinculacion || null,
    jornada_turno: form.jornada_turno || null,
    cargo: form.cargo || null,
    cargo_descripcion: form.cargo_descripcion || null,
    area: form.area || null,
    sede: form.sede || null,
    nivel_educativo: form.nivel_educativo || null,
    genero: form.genero || null,
    fecha_nacimiento: form.fecha_nacimiento || null,
    eps: form.eps || null,
    afp: form.afp || null,
    arl: form.arl || null,
  }
}

/** El autorregistro es insumo: el operador corrige cualquier respuesta, en un solo PATCH. */
export function FichaEditDialog({
  businessId,
  employeeId,
  employeeName,
  ficha,
}: {
  businessId: string
  employeeId: string
  employeeName: string
  ficha: Ficha
}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState<FichaFormState>(() => fichaToForm(ficha))
  const [fechaNacimientoError, setFechaNacimientoError] = useState<string | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)
  const {data: areas} = useTagCatalog(businessId, 'area', open)
  const {data: sedes} = useTagCatalog(businessId, 'sede', open)
  const {data: positions} = useTagCatalog(businessId, 'position', open)
  const update = useUpdateEmployeeFicha(businessId, employeeId)

  function handleOpenChange(next: boolean) {
    setOpen(next)
    setError(null)
    setFechaNacimientoError(undefined)
    if (next) setForm(fichaToForm(ficha))
  }

  function set(field: keyof FichaFormState, value: string) {
    setForm((f) => ({...f, [field]: value}))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const problem = validateFechaNacimiento(form.fecha_nacimiento)
    setFechaNacimientoError(problem ?? undefined)
    if (problem) return
    setError(null)
    update.mutate(fichaFormToPayload(form), {
      onSuccess: () => handleOpenChange(false),
      onError: (err) => setError(translateApiError(err)),
    })
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" data-testid="employee-ficha-edit-open">
          {t('employees.ficha.edit')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('employees.ficha.editTitle', {name: employeeName})}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <p className="text-xs text-muted-foreground">{t('employees.ficha.editHint')}</p>

          <CatalogOtherField
            id="ficha-edit-cargo"
            label={t('ficha.cargo')}
            options={(positions ?? []).map((p) => p.name)}
            value={form.cargo}
            onChange={(v) => set('cargo', v)}
            onCommit={(v) => set('cargo', v)}
          />
          <div className="space-y-2">
            <Label htmlFor="ficha-edit-cargo-descripcion">{t('ficha.cargoDescripcion')}</Label>
            <textarea
              id="ficha-edit-cargo-descripcion"
              maxLength={2000}
              value={form.cargo_descripcion}
              onChange={(e) => set('cargo_descripcion', e.target.value)}
              className="min-h-20 w-full rounded-lg border border-input bg-transparent px-2.5 py-2 text-sm text-foreground"
            />
          </div>
          <CatalogOtherField
            id="ficha-edit-area"
            label={t('ficha.area')}
            options={(areas ?? []).map((a) => a.name)}
            value={form.area}
            onChange={(v) => set('area', v)}
            onCommit={(v) => set('area', v)}
          />
          <CatalogOtherField
            id="ficha-edit-sede"
            label={t('ficha.sede')}
            options={(sedes ?? []).map((s) => s.name)}
            value={form.sede}
            onChange={(v) => set('sede', v)}
            onCommit={(v) => set('sede', v)}
          />
          <DateField
            id="ficha-edit-fecha-ingreso"
            label={t('ficha.fechaIngreso')}
            value={form.fecha_ingreso}
            max={localToday()}
            onChange={(v) => set('fecha_ingreso', v)}
          />
          <div className="space-y-2">
            <Label htmlFor="ficha-edit-tipo-vinculacion">{t('ficha.tipoVinculacion')}</Label>
            <select
              id="ficha-edit-tipo-vinculacion"
              value={form.tipo_vinculacion}
              onChange={(e) => set('tipo_vinculacion', e.target.value)}
              className="h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground">
              <option value="">{t('employees.contractType.unspecified')}</option>
              {CONTRACT_TYPES.map((c) => (
                <option key={c} value={c}>
                  {t(`employees.contractType.${c}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="ficha-edit-jornada-turno">{t('ficha.jornadaTurno')}</Label>
            <select
              id="ficha-edit-jornada-turno"
              value={form.jornada_turno}
              onChange={(e) => set('jornada_turno', e.target.value)}
              className="h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground">
              <option value="">{t('ficha.jornadaOptions.unspecified')}</option>
              {JORNADA_TYPES.map((j) => (
                <option key={j} value={j}>
                  {t(`ficha.jornadaOptions.${j}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="ficha-edit-nivel-educativo">{t('ficha.nivelEducativo')}</Label>
            <select
              id="ficha-edit-nivel-educativo"
              value={form.nivel_educativo}
              onChange={(e) => set('nivel_educativo', e.target.value)}
              className="h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground">
              <option value="">{t('ficha.nivelEducativoOptions.unspecified')}</option>
              {NIVEL_EDUCATIVO.map((n) => (
                <option key={n} value={n}>
                  {t(`ficha.nivelEducativoOptions.${n}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="ficha-edit-genero">{t('ficha.genero')}</Label>
            <select
              id="ficha-edit-genero"
              value={form.genero}
              onChange={(e) => set('genero', e.target.value)}
              className="h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground">
              <option value="">{t('ficha.generoOptions.unspecified')}</option>
              {GENERO.map((g) => (
                <option key={g} value={g}>
                  {t(`ficha.generoOptions.${g}`)}
                </option>
              ))}
            </select>
          </div>
          <DateField
            id="ficha-edit-fecha-nacimiento"
            label={t('ficha.fechaNacimiento')}
            value={form.fecha_nacimiento}
            max={localToday()}
            onChange={(v) => {
              set('fecha_nacimiento', v)
              setFechaNacimientoError(validateFechaNacimiento(v) ?? undefined)
            }}
          />
          <FieldError error={fechaNacimientoError} />
          <CatalogOtherField
            id="ficha-edit-eps"
            label={t('ficha.eps')}
            options={EPS_OPTIONS}
            value={form.eps}
            onChange={(v) => set('eps', v)}
            onCommit={(v) => set('eps', v)}
          />
          <CatalogOtherField
            id="ficha-edit-afp"
            label={t('ficha.afp')}
            options={AFP_OPTIONS}
            value={form.afp}
            onChange={(v) => set('afp', v)}
            onCommit={(v) => set('afp', v)}
          />
          {form.tipo_vinculacion === 'prestacion_servicios' && (
            <div data-testid="ficha-edit-arl-field">
              <CatalogOtherField
                id="ficha-edit-arl"
                label={t('ficha.arl')}
                options={ARL_OPTIONS}
                value={form.arl}
                onChange={(v) => set('arl', v)}
                onCommit={(v) => set('arl', v)}
                hint={t('ficha.arlHelp')}
              />
            </div>
          )}

          <FormDialogFooter
            error={error}
            pending={update.isPending}
            submitLabel={t('common.save')}
            onCancel={() => handleOpenChange(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}
